import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLang } from '../lib/lang';
import InlineEditable from './InlineEditable';
import CountdownTimer from './CountdownTimer';
import { Button } from './ui/button';
import { Sparkles, Trophy, Ticket, Users, ArrowRight, Ban } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

// Immersive stacked hero — every live competition shown as a full-width band
// (winuwatch.com inspiration). No carousel: everything visible while scrolling.
export default function HeroShowcase({ raffles = [], settings }) {
  const { t } = useLang();
  const pointRate = Number(settings?.point_rate) || 1;

  return (
    <section className="relative overflow-hidden bg-rolex-green" data-testid="home-hero">
      <div className="absolute inset-0 hero-spotlight pointer-events-none" />
      <div className="absolute inset-0 noise-overlay opacity-60 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative">
        {/* ── Header ── */}
        <motion.div
          initial="hidden" animate="visible" variants={fadeUp}
          className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
        >
          <div className="gold-line mb-5 justify-center">
            <Sparkles className="w-3 h-3 text-gold" />
            <InlineEditable tKey="heroBadge">{t('heroBadge') || "UK's Premier Luxury Competitions"}</InlineEditable>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-cream">
            <InlineEditable tKey="heroTitle1">{t('heroTitle1')}</InlineEditable>{' '}
            <span className="text-gold-gradient italic"><InlineEditable tKey="heroTitle2">{t('heroTitle2')}</InlineEditable></span>{' '}
            <InlineEditable tKey="heroTitle3">{t('heroTitle3')}</InlineEditable>
          </h1>
          <p className="text-base sm:text-lg text-cream/65 mt-5 leading-relaxed font-light">
            <InlineEditable tKey="heroDesc" multiline>{t('heroDesc')}</InlineEditable>
          </p>
          <div className="mt-7 inline-flex items-center gap-2 rounded-full border border-gold/30 bg-cream/[0.04] px-4 py-2 backdrop-blur-sm">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-70" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-gold" />
            </span>
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-gold">
              {raffles.length} {t('drawsLive') || 'draws live'}
            </span>
          </div>
        </motion.div>

        {/* ── Bands ── */}
        <div className="space-y-8 sm:space-y-10">
          {raffles.map((raffle, i) => {
            const isComingSoon = raffle.status === 'coming_soon';
            const maxTickets = Number(raffle.max_tickets || 0);
            const soldTickets = Number(raffle.sold_tickets || 0);
            const isSoldOut = raffle.force_sold_out || (maxTickets > 0 && soldTickets >= maxTickets);
            const reversed = i % 2 === 1;
            const ticketPrice = Number(raffle.ticket_cost_points || 0) / pointRate;
            const priceLabel = Number.isInteger(ticketPrice) ? ticketPrice.toLocaleString() : ticketPrice.toFixed(2);
            const countdownDate = isSoldOut ? (raffle.draw_date || null) : (raffle.ends_at || null);

            return (
              <motion.div
                key={raffle._id || i}
                initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}
                variants={fadeUp}
                data-testid="hero-band"
                className="rounded-[1.75rem] overflow-hidden border border-gold/25 bg-rolex-green-deep shadow-[0_40px_90px_-30px_rgba(0,0,0,0.75)] transition-all duration-500 hover:border-gold/50 hover:shadow-[0_50px_110px_-25px_rgba(184,138,42,0.30)]"
              >
                <div className={`grid grid-cols-1 lg:grid-cols-[1.25fr_0.9fr] ${reversed ? 'lg:[direction:rtl]' : ''}`}>
                  {/* Image */}
                  <Link
                    to={`/raffle/${raffle._id}`}
                    className={`group relative block aspect-[16/11] lg:h-full lg:min-h-[430px] overflow-hidden hero-shimmer ${reversed ? 'lg:[direction:ltr]' : ''}`}
                  >
                    <img
                      src={raffle.images?.[0]}
                      alt={raffle.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1300ms] ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-transparent pointer-events-none" />

                    {/* Value sticker */}
                    <div className="absolute top-4 right-4 z-10 bg-gold-gradient text-rolex-green rounded-2xl px-3.5 py-2 shadow-xl border-2 border-rolex-green/30 text-right">
                      <p className="text-[8px] uppercase tracking-[0.2em] font-extrabold leading-none mb-0.5">
                        <Trophy className="w-2.5 h-2.5 inline mr-0.5" /> {t('worth') || 'Worth'}
                      </p>
                      <p className="font-mono text-lg sm:text-xl font-black leading-none">£{Number(raffle.product_value || 0).toLocaleString()}</p>
                    </div>

                    {/* Coming soon badge */}
                    {isComingSoon && (
                      <div className="absolute top-4 left-4 z-10 inline-flex items-center gap-1 rounded-full bg-rolex-green/80 text-gold border border-gold/50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider backdrop-blur-sm">
                        {t('comingSoon') || 'Coming Soon'}
                      </div>
                    )}

                    {/* Sold out — full-width diagonal banner + dark veil */}
                    {isSoldOut && !isComingSoon && (
                      <div className="absolute inset-0 z-10 overflow-hidden pointer-events-none">
                        <div className="absolute inset-0 bg-black/55 backdrop-blur-[1px]" />
                        <div className="absolute left-1/2 top-1/2 w-[180%] -translate-x-1/2 -translate-y-1/2 -rotate-[10deg] bg-gradient-to-r from-red-800 via-red-600 to-red-800 border-y-[3px] border-white/50 shadow-[0_10px_40px_rgba(0,0,0,0.5)] py-3.5">
                          <div className="flex items-center justify-center gap-4 text-white">
                            <Ban className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" />
                            <span className="font-display text-2xl sm:text-3xl font-black uppercase tracking-[0.3em] whitespace-nowrap [text-shadow:0_2px_8px_rgba(0,0,0,0.5)]">{t('soldOutBig') || 'Sold Out'}</span>
                            <Ban className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Category + product title */}
                    <div className="absolute bottom-0 inset-x-0 p-5 sm:p-6 z-10">
                      {raffle.category && (
                        <p className="text-[10px] sm:text-xs uppercase tracking-[0.28em] font-bold text-gold mb-1.5">{raffle.category}</p>
                      )}
                      <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold text-cream leading-tight [text-shadow:0_2px_10px_rgba(0,0,0,0.7)]">
                        {raffle.title}
                      </h3>
                    </div>
                  </Link>

                  {/* Info panel */}
                  <div className={`relative overflow-hidden isolate bg-gradient-to-br from-[#0A2C20] to-[#04120C] p-6 sm:p-8 flex flex-col justify-center ${reversed ? 'lg:[direction:ltr]' : ''}`}>
                    <div className="pointer-events-none absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[radial-gradient(circle,hsl(var(--gold)/0.14),transparent_65%)] -z-[1]" />
                    <div className="flex items-center gap-2 mb-4">
                      <span className="h-px w-6 bg-gold/60" />
                      <p className="text-[10px] text-gold uppercase tracking-[0.25em] font-bold">
                        <InlineEditable tKey="featuredDraw">{t('featuredDraw')}</InlineEditable>
                      </p>
                    </div>

                    {countdownDate ? (
                      <div className="rounded-2xl border border-gold/15 bg-black/20 p-3">
                        <CountdownTimer endDate={countdownDate} mode="card" soldOut={isSoldOut} className="text-cream" />
                      </div>
                    ) : (
                      <div className="rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-3 text-center">
                        <p className="text-sm font-semibold text-amber-300">
                          <InlineEditable tKey="drawDateSoon">{t('drawDateSoon') || 'Date du tirage à venir'}</InlineEditable>
                        </p>
                      </div>
                    )}

                    <p className="text-[11px] italic text-cream/45 mt-3">
                      <InlineEditable tKey="orUntilSoldOut">{t('orUntilSoldOut') || 'or until all tickets are sold out'}</InlineEditable>
                    </p>

                    {/* Price + max tickets */}
                    <div className="grid grid-cols-2 gap-3 mt-6">
                      <div className="rounded-xl bg-gold-gradient/15 border-2 border-gold/50 p-3.5 text-center shadow-lg">
                        <p className="text-[9px] uppercase tracking-[0.2em] font-extrabold text-gold mb-1">
                          <Ticket className="w-3 h-3 inline mr-0.5" /> <InlineEditable tKey="ticketPrice">{t('ticketPrice') || 'Ticket price'}</InlineEditable>
                        </p>
                        <p className="font-mono text-2xl sm:text-3xl font-black text-gold-gradient leading-none">£{priceLabel}</p>
                      </div>
                      <div className="rounded-xl bg-cream/[0.06] border-2 border-cream/25 p-3.5 text-center shadow-lg">
                        <p className="text-[9px] uppercase tracking-[0.2em] font-extrabold text-cream/85 mb-1">
                          <Users className="w-3 h-3 inline mr-0.5" /> <InlineEditable tKey="ticketsMax">{t('ticketsMax') || 'Max tickets'}</InlineEditable>
                        </p>
                        <p className="font-mono text-2xl sm:text-3xl font-black text-cream leading-none">{maxTickets.toLocaleString()}</p>
                      </div>
                    </div>

                    {/* CTA */}
                    <Link to={`/raffle/${raffle._id}`} className="block mt-6">
                      <Button
                        size="lg"
                        className="w-full btn-gold btn-press h-14 rounded-xl gap-2 text-sm font-bold uppercase tracking-[0.1em] shadow-[0_12px_30px_-8px_rgba(184,138,42,0.55)]"
                        data-testid="hero-band-cta-button"
                      >
                        {isComingSoon ? (
                          <InlineEditable tKey="comingSoon">{t('comingSoon') || 'Coming Soon'}</InlineEditable>
                        ) : isSoldOut ? (
                          <InlineEditable tKey="viewDraw">{t('viewDraw') || 'View draw'}</InlineEditable>
                        ) : (
                          <>
                            <InlineEditable tKey="getYourTicket">{t('getYourTicket') || 'Get your ticket'}</InlineEditable>
                            <ArrowRight className="w-4 h-4 shrink-0" />
                          </>
                        )}
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
